import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    TextField, Button, Box, Typography, Alert,
    Paper, Container, Fade, Link, keyframes
} from '@mui/material';
import { LockReset, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../api/config';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

/**
 * Смена пароля по одноразовой ссылке из письма.
 *
 * Токен приходит в query-параметре `token`: его выдаёт бэкенд и хранит только
 * в виде хеша, поэтому повторный переход по той же ссылке отклоняется.
 */
const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirm) {
            setStatus('error');
            setMessage('Пароли не совпадают.');
            return;
        }

        setStatus('loading');
        try {
            //  API: POST /api/v1/auth/reset-password (базовый адрес — из окружения)
            await axios.post(`${API_URL}/v1/auth/reset-password`, { token, password });
            setStatus('success');
            setMessage('Пароль обновлён. Войдите с новым паролем.');
        } catch (err: any) {
            setStatus('error');
            setMessage(err.response?.data?.message || 'Не удалось сбросить пароль. Запросите новую ссылку.');
        }
    };

    return (
        <Box sx={{
            minHeight: 'calc(100vh - var(--orion-offline-banner-height, 0px))',
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f4f7f6',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org fill='none' fill-rule='evenodd'%3E%3Cg fill='%231976d2' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}>
            <Container maxWidth="xs">
                <Fade in={true} timeout={800}>
                    <Paper elevation={0} sx={{
                        p: 4,
                        borderRadius: 4,
                        textAlign: 'center',
                        border: '1px solid rgba(25, 118, 210, 0.12)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
                    }}>
                        <Box sx={{ animation: `${pulse} 3s infinite ease-in-out`, mb: 2 }}>
                            <LockReset sx={{ fontSize: 48, color: '#1976d2' }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
                            Новый пароль
                        </Typography>

                        {!token ? (
                            <Box>
                                <Alert severity="error" sx={{ mt: 2, mb: 3, borderRadius: 2 }}>
                                    Ссылка недействительна. Запросите новую.
                                </Alert>
                                <Button component={RouterLink} to="/forgot-password" variant="outlined" fullWidth sx={{ borderRadius: 3 }}>
                                    Запросить ссылку
                                </Button>
                            </Box>
                        ) : status === 'success' ? (
                            <Fade in={true}>
                                <Box>
                                    <Alert severity="success" sx={{ mt: 2, mb: 3, borderRadius: 2 }}>
                                        {message}
                                    </Alert>
                                    <Button component={RouterLink} to="/login" variant="outlined" fullWidth sx={{ borderRadius: 3 }}>
                                        Войти
                                    </Button>
                                </Box>
                            </Fade>
                        ) : (
                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                                    Придумайте новый пароль для входа в личный кабинет.
                                </Typography>

                                {status === 'error' && (
                                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{message}</Alert>
                                )}

                                <TextField
                                    label="Новый пароль"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    helperText="Не короче 6 символов"
                                    required
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />

                                <TextField
                                    label="Повторите пароль"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={status === 'loading'}
                                    sx={{
                                        mt: 4,
                                        py: 1.5,
                                        borderRadius: 3,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                    }}
                                >
                                    {status === 'loading' ? 'Сохранение...' : 'Сменить пароль'}
                                </Button>

                                <Box sx={{ mt: 3 }}>
                                    <Link component={RouterLink} to="/login" sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        color: 'text.secondary',
                                        '&:hover': { color: '#1976d2' }
                                    }}>
                                        <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} /> Назад к входу
                                    </Link>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;
